<template>
	<div class="stats-badge" :class="skin">
		<Tooltip
			:placement="Placements.TOP"
			:disabled="getTooltipText(statsType).length <= 0"
			class="tooltip"
			:heading="getTooltipHeading(statsType)"
		>
			<template #element>
				<div
					class="stats-badge__inset-box"
					:class="`stats-badge__inset-box--${pentagonShape}`"
				>
					<div
						:class="`background-section--${skin}`"
						class="background-section"
						:style="background"
					/>
					<AppMainIcon
						class="stats-badge__icon"
						:iconName="getStatsIcon(statsType)"
						:iconSize="16"
					/>
					<div class="stats-badge__content">
						<div class="stats-badge__main-value">
							{{ statsValues[0] }}
						</div>
						<div
							v-if="statsValues[1] !== 0"
							:class="[
								'stats-badge__bonus-value',
								{
									'stats-badge__bonus-value--positive':
										statsValues[1] >= 0,
									'stats-badge__bonus-value--negative':
										statsValues[1] < 0,
								},
							]"
						>
							{{
								(statsValues[1] >= 0 ? "+" : "-") +
								Math.abs(statsValues[1])
							}}
						</div>
					</div>
				</div>
			</template>
			<template #tooltip>
				<span>
					{{ getTooltipText(statsType) }}
				</span>
			</template>
		</Tooltip>
	</div>
</template>

<script lang="ts" setup>
import AppMainIcon from "@/components/AppMainIcon.vue";
import Tooltip from "@/components/Tooltip.vue";
import { IMAGES_CDN_URL } from "@/config/constants";
import { type ArmorOrAttackNumber } from "@/types/tile.ts";
import { Placements } from "@/types/ui.ts";
import { translate } from "@/utils/translate";
import { computed } from "vue";

type StatsType = "swords" | "melee" | "ranged" | "siege" | "default";
export type StatsSkin =
	| "friendly-light"
	| "friendly-dark"
	| "enemy-special-light"
	| "enemy-special-dark"
	| "enemy-light"
	| "enemy-dark";
const { statsType, statsValues, skin } = defineProps<{
	statsType: StatsType;
	statsValues: [ArmorOrAttackNumber, ArmorOrAttackNumber];
	skin: StatsSkin;
}>();

const background = computed(() => {
	return {
		backgroundImage: `url('${IMAGES_CDN_URL}/backgrounds/fight-system/${skin}.avif?v2')`,
		backgroundSize: "cover",
		backgroundPosition: "center",
		height: skin.includes("light")
			? "calc(100% - 0.25rem)"
			: "calc(100% - 0.375rem)",
	};
});

const pentagonShape = computed((): "light" | "dark" => {
	if (skin.includes("light")) return "light";
	return "dark";
});

const getStatsIcon = (statsType: StatsType): string => {
	switch (statsType) {
		case "swords":
			return "swords";
		case "melee":
			return "purple-shield";
		case "ranged":
			return "orange-shield";
		case "siege":
			return "green-shield";
		default:
			return "blue-shield";
	}
};

const getTooltipText = (statsType: StatsType): string => {
	switch (statsType) {
		case "swords":
			return translate("ui.attack_description");
		case "melee":
			return translate("ui.meleearmor_description");
		case "ranged":
			return translate("ui.rangedarmor_description");
		case "siege":
			return translate("ui.siegearmor_description");
		case "default":
			return translate("ui.armor_description");
		default:
			return "";
	}
};
const getTooltipHeading = (statsType: StatsType): string => {
	switch (statsType) {
		case "swords":
			return translate("ui.attack");
		case "melee":
			return translate("ui.melee_armor");
		case "ranged":
			return translate("ui.ranged_armor");
		case "siege":
			return translate("ui.siege_armor");
		case "default":
			return translate("ui.armor");
		default:
			return "";
	}
};
</script>

<style lang="scss" scoped>
.stats-badge {
	flex-grow: 1;
	@include flex-row-center;

	&__inset-box {
		@include flex-col-center;
		position: relative;
		padding: 0.375rem 0 0.25rem;
		// width: 4.75rem;
		width: 100%;
		font-weight: bold;
		font-size: 1rem;
		line-height: 1;

		&--dark {
			// width: 4.3125rem;
		}
	}

	&__content {
		display: flex;
		padding-bottom: 0.25rem;
	}
	&__main-value {
		z-index: 1;
	}

	&__icon {
		z-index: 1;
	}

	&__bonus-value {
		z-index: 1;
	}

	&__bonus-value--positive {
		color: #19b345;
	}

	&__bonus-value--negative {
		color: #df101e;
	}
	.background-section {
		position: absolute;
		top: 0;
		top: 0.625rem;
		z-index: 0;
		width: 100%;
	}

	:deep(.tooltip) {
		width: 100%;
	}
}
</style>
